import React from 'react';
import {
  Box, Grid, Button, Divider, CircularProgress, Typography,
  TextField, Checkbox, FormControlLabel, FormControl, InputLabel,
  Select, MenuItem, FormHelperText, Paper, InputAdornment
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
  AccountBalanceWallet,
  VisibilityOutlined,
} from '@mui/icons-material';
import { getRequest, postRequest, putRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL, PUT_URL } from 'Includes/urls';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
// Billing cycle options
const BILLING_CHOICES = [
  { id: 'monthly', name: 'Monthly' },
  { id: 'termly', name: 'Termly' },
  { id: 'one_time', name: 'One-time' },
];

function ServicePlanAdd(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const [submitDisable, setSubmitDisable] = React.useState(false);
  const [selectedErpFeatures, setSelectedErpFeatures] = React.useState([]);
  const [selectedServices, setSelectedServices] = React.useState({});
  const [featureType, setFeatureType] = React.useState({
    erp: true,       // default checked
    digital: false,
  });
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [planId, setPlanId] = React.useState(null);
  const [planLoading, setPlanLoading] = React.useState(false);
  const [serviceList, setServiceList] = React.useState([]);
  const [serviceLoading, setServiceLoading] = React.useState(true);
  const [serviceMode, setServiceMode] = React.useState('existing');
  const [newService, setNewService] = React.useState({
    name: '',
    code: '',
    description: '',
  });
  const [taxList, setTaxList] = React.useState([]);
  const [ERPFeatureList, setERPFeatureList] = React.useState([]);
  const [DigitalMarketingFeatureList, setDigitalMarketingFeatureList] = React.useState([]);
  const [formData, setFormData] = React.useState({
    service: '',
    name: '',
    billing_name: '',
    billing_cycle: 'monthly',
    base_price: '',
    currency: 'INR',
    default_is_gst_applicable: true,
    default_tax: '',
    default_hsn_sac: '',
    is_base_amount_to_be_reset_after_year: true,
    has_maintenance_charges: false,
    description: '',
    is_visible: true,
    service_type: 'SERVICE_WISE',
  });

  const [errors, setErrors] = React.useState({});
  const erpCategoriesEntries = React.useMemo(() => {
    const grouped = (ERPFeatureList || []).reduce((acc, item) => {
      const cat = (item.category || '').trim() || 'General';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  }, [ERPFeatureList]);

  const erpCategoryRows = React.useMemo(() => {
    const rows = [];
    const size = 3;
    for (let i = 0; i < erpCategoriesEntries.length; i += size) {
      rows.push(erpCategoriesEntries.slice(i, i + size));
    }
    return rows;
  }, [erpCategoriesEntries]);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');
    if (id) {
      setIsEditMode(true);
      setPlanId(id);
    }
  }, [location.search]);

  React.useEffect(() => {
    getServiceList();
    getTaxList();
    getERPFeatureList();
    getDigitalMarketingFeatureList();
  }, []);

  React.useEffect(() => {
    if (planId && serviceList.length > 0) {
      getPlanDetails(planId);
    }
  }, [planId, serviceList]);

  const getERPFeatureList = () => {
    const url = GET_URL.erpfeature?.api;
    getRequest(url, {}, props).then(response => {
      setERPFeatureList(response.data.data || []);
    });
  };

  const getServiceList = () => {
    const url = GET_URL.services?.api;
    getRequest(url, {}, props).then(response => {
      setServiceLoading(false);
      if (response && response.status === 200) {
        setServiceList(response.data.data || []);
      }
    }).catch(() => setServiceLoading(false));
  };

  const getDigitalMarketingFeatureList = () => {
    const url = GET_URL.digitalmarketingfeature?.api;
    getRequest(url, {}, props).then(response => {
      setDigitalMarketingFeatureList(response.data.data || []);
    });
  };

  const getTaxList = () => {
    const url = GET_URL.taxes?.api;
    getRequest(url, {}, props).then(response => {
      if (response && response.status === 200) {
        setTaxList(response.data.data || []);
      }
    });
  };

  const getPlanDetails = (id) => {
    setPlanLoading(true);
    const url = `${GET_URL.plans.api}${id}/`;
    getRequest(url, {}, props).then(response => {
      setPlanLoading(false);
      if (response && response.status === 200) {
        const planData = response.data.data || response.data;
        setFormData({
          service: planData.service?.id || planData.service || '',
          name: planData.name || '',
          billing_name: planData.billing_name || '',
          billing_cycle: planData.billing_cycle || 'monthly',
          service_type: planData.service_type || 'SERVICE_WISE',
          base_price: planData.base_price != null ? String(planData.base_price) : '',
          currency: planData.currency || 'INR',
          default_is_gst_applicable: planData.default_is_gst_applicable ?? true,
          default_tax: planData.default_tax?.id || planData.default_tax || '',
          default_hsn_sac: planData.default_hsn_sac || '',
          is_base_amount_to_be_reset_after_year: planData.is_base_amount_to_be_reset_after_year ?? true,
          has_maintenance_charges: planData.has_maintenance_charges ?? false,
          description: planData.description || '',
          is_visible: planData.is_visible ?? true,
        });
      }
    }).catch(() => {
      setPlanLoading(false);
      Swal.fire({ type: 'error', title: 'Error', text: 'Failed to fetch plan details' });
    });
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    let isValid = true;
    if (!isEditMode) {
      if (serviceMode === 'existing') {
        if (!formData.service) { newErrors.service = 'Please select a service'; isValid = false; }
      } else {
        if (!String(newService.name || '').trim()) { newErrors.service_name = 'Service Name is required'; isValid = false; }
        if (!String(newService.code || '').trim()) { newErrors.service_code = 'Service Code is required'; isValid = false; }
      }
    } else if (!formData.service) {
      newErrors.service = 'Please select a service'; isValid = false;
    }
    if (!formData.name?.trim()) { newErrors.name = 'Plan Name is required'; isValid = false; }
    if (!formData.default_tax) { newErrors.default_tax = 'Tax selection required'; isValid = false; }
    
    const basePriceStr = String(formData.base_price ?? '').trim();
    if (!basePriceStr || parseFloat(basePriceStr) <= 0) {
      newErrors.base_price = 'Valid price required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const submit = async () => {
    if (validate()) {
      setSubmitDisable(true);
      try {
      let serviceId = formData.service;
      if (!isEditMode && serviceMode === 'new') {
        const created = await postRequest(
          POST_URL.services?.api,
          [{
            name: String(newService.name || '').trim(),
            code: String(newService.code || '').trim(),
            service_description: String(newService.description || '').trim(),
          }],
          props
        );
        if (!(created && created.status === 200)) {
          throw new Error('Unable to create service');
        }
        const listRes = await getRequest(GET_URL.services?.api, {}, props);
        const list = listRes?.data?.data || [];
        const matched = (list || []).find(
          (s) => String(s.code || '').toLowerCase() === String(newService.code || '').trim().toLowerCase()
        );
        if (!matched?.id) {
          throw new Error('Service created but not found in list');
        }
        serviceId = matched.id;
      }
      const url = isEditMode ? `${PUT_URL.plans?.api}${planId}/` : POST_URL.plans.api;
      const method = isEditMode ? putRequest : postRequest;
      const payload = {
        ...formData,
        service: serviceId,
        billing_name: String(formData.billing_name || formData.name || '').trim(),
        service_type: 'SERVICE_WISE',
        currency: 'INR',
        default_hsn_sac: '',
        is_base_amount_to_be_reset_after_year: true,
        has_maintenance_charges: false,
        is_visible: true,
      
        // ERP → map IDs to names
        erp_features: featureType.erp
          ? selectedErpFeatures.map(id => {
              const feature = ERPFeatureList.find(f => f.id === id);
              return feature ? feature.name : null;
            }).filter(Boolean)
          : [],
      
        // Digital → map selected IDs to names
        digital_marketing_features: featureType.digital
          ? Object.keys(selectedServices)
              .filter(id => selectedServices[id])
              .map(id => {
                const feature = DigitalMarketingFeatureList.find(f => f.id === Number(id));
                return feature ? {
                  id: feature.id,
                  name: feature.name,
                  calculation_type: feature.calculation_type || 'monthwise',
                } : null;
              })
              .filter(Boolean)
          : [],
      };
      
      method(url, payload, props).then(response => {
        if (response && response.status === 200) {
          Swal.fire({
            type: 'success',
            title: response.data.Reason || 'Success',
            text: `Service Plan ${isEditMode ? 'updated' : 'added'} successfully`,
            timer: 1500,
            showConfirmButton: false
          });
          navigate('/billing/service-plan/list');
        }
      }).catch(() => {}).finally(() => setSubmitDisable(false));
      } catch (e) {
        setSubmitDisable(false);
        Swal.fire({
          type: 'error',
          title: 'Error',
          text: e?.message || 'Unable to save service plan',
        });
      }
    }
  };

  const resetForm = () => {
    if (isEditMode && planId) getPlanDetails(planId);
    else setFormData({
      service: '', name: '', billing_name: '', billing_cycle: 'monthly',
      service_type: 'SERVICE_WISE', base_price: '', currency: 'INR',
      default_is_gst_applicable: true, default_tax: '', default_hsn_sac: '',
      is_base_amount_to_be_reset_after_year: true, has_maintenance_charges: false,
      description: '', is_visible: true,
    });
    setErrors({});
    setServiceMode('existing');
    setNewService({ name: '', code: '', description: '' });
  };

  const selectedService = serviceList.find(s => s.id === formData.service);

  const goToPlanListOrBack = () => {
    if (navigate.length > 1) {
      navigate(-1);
    } else {
      navigate('/billing/service-plan/list');
    }
  };

  const sectionCardSx = {
    mb: 3,
    p: 2,
    borderRadius: 2,
    borderColor: 'divider',
    bgcolor: 'grey.50',
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        width: '100%',
        bgcolor: 'background.default',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#0a0a0a' }}>
            {isEditMode ? 'Edit service plan' : 'Add service plan'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            Service plans are <strong>boilerplates</strong> used when creating agreements. Each plan links to a catalog <strong>service</strong>{' '}
            (what line it belongs to—e.g. ERP) and a named <strong>plan</strong> (the package). Turn on ERP modules vs other services independently;
            agreements then combine these with ID card pricing as three main offerings.
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap" alignSelf={{ xs: 'flex-start', sm: 'center' }}>
          <Button
            variant="contained"
            startIcon={<VisibilityOutlined />}
            onClick={goToPlanListOrBack}
            sx={{ textTransform: 'none', borderRadius: 2, px: 2.5, py: 1 }}
          >
            View service plans
          </Button>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {planLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper elevation={0} variant="outlined" sx={sectionCardSx}>
              <Typography variant="subtitle2" sx={{ color: '#0a0a0a', fontWeight: 700 }}>
                Catalog service (line of business)
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
                Pick the <strong>service</strong> this plan sits under (e.g. your main ERP service in the catalog). The <strong>plan name</strong> in the next
                section is the specific package name—a template label for agreements—not the same thing as the service.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  {isEditMode ? (
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'grey.100',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Linked catalog service
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {selectedService?.name || '-'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Code: {selectedService?.code || '-'}
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small" variant="outlined">
                          <InputLabel>Service source</InputLabel>
                          <Select
                            value={serviceMode}
                            onChange={(e) => setServiceMode(e.target.value)}
                            label="Service source"
                          >
                            <MenuItem value="existing">Use existing service</MenuItem>
                            <MenuItem value="new">Add new service here</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {serviceMode === 'existing' ? (
                        <Grid item xs={12}>
                          <FormControl fullWidth size="small" variant="outlined" error={!!errors.service} required>
                            <InputLabel>Catalog service</InputLabel>
                            <Select
                              value={formData.service}
                              onChange={(e) => handleFieldChange('service', e.target.value)}
                              label="Catalog service"
                              disabled={serviceLoading}
                            >
                              {serviceList.map((s) => (
                                <MenuItem key={s.id} value={s.id}>{s.name} ({s.code})</MenuItem>
                              ))}
                            </Select>
                            {errors.service && <FormHelperText>{errors.service}</FormHelperText>}
                          </FormControl>
                        </Grid>
                      ) : (
                        <>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Service Name"
                              size="small"
                              fullWidth
                              required
                              value={newService.name}
                              onChange={(e) => setNewService((p) => ({ ...p, name: e.target.value }))}
                              error={!!errors.service_name}
                              helperText={errors.service_name || ''}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Service Code"
                              size="small"
                              fullWidth
                              required
                              value={newService.code}
                              onChange={(e) => setNewService((p) => ({ ...p, code: e.target.value }))}
                              error={!!errors.service_code}
                              helperText={errors.service_code || 'Use unique code'}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Service Description (optional)"
                              size="small"
                              fullWidth
                              multiline
                              minRows={2}
                              value={newService.description}
                              onChange={(e) => setNewService((p) => ({ ...p, description: e.target.value }))}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Paper>

            {!isEditMode && serviceMode === 'existing' && !formData.service && !serviceLoading && (
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Please select a service to begin configuration.
                </Typography>
              </Box>
            )}

            {(isEditMode || serviceMode === 'new' || formData.service) && (
              <>
                <Paper elevation={0} variant="outlined" sx={sectionCardSx}>
                  <Typography variant="subtitle2" sx={{ color: '#0a0a0a', fontWeight: 700 }}>
                    ERP and other services on this plan
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
                    These are different buckets: <strong>ERP</strong> attaches Edubricz <strong>ERP modules</strong> to the template.{' '}
                    <strong>Other services</strong> attaches separate non-ERP catalog items. ID card is not defined here—it is configured directly on each agreement.
                  </Typography>
                  <Box display="flex" gap={3} flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={featureType.erp}
                          onChange={(e) =>
                            setFeatureType(prev => ({
                              ...prev,
                              erp: e.target.checked
                            }))
                          }
                        />
                      }
                      label="Include ERP modules"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={featureType.digital}
                          onChange={(e) =>
                            setFeatureType(prev => ({
                              ...prev,
                              digital: e.target.checked
                            }))
                          }
                        />
                      }
                      label="Include other services (non-ERP)"
                    />
                  </Box>
                </Paper>

                <Paper elevation={0} variant="outlined" sx={sectionCardSx}>
                  <Typography variant="subtitle2" sx={{ color: '#0a0a0a', fontWeight: 700 }}>
                    Plan details
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
                    <strong>Plan name</strong> = the boilerplate package label. Billing display name, cycle, base price, and description.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Service plan name (package / template)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        required
                        value={formData.name}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                        error={!!errors.name}
                        helperText={errors.name || 'Shown when picking a boilerplate on agreements'}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Billing display name"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={formData.billing_name}
                        onChange={(e) => handleFieldChange('billing_name', e.target.value)}
                        helperText="Optional; defaults to plan name"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small" required variant="outlined">
                        <InputLabel>Billing cycle</InputLabel>
                        <Select
                          value={formData.billing_cycle}
                          onChange={(e) => handleFieldChange('billing_cycle', e.target.value)}
                          label="Billing cycle"
                        >
                          {BILLING_CHOICES.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Base price"
                        variant="outlined"
                        size="small"
                        fullWidth
                        required
                        type="number"
                        value={formData.base_price}
                        onChange={(e) => handleFieldChange('base_price', e.target.value)}
                        error={!!errors.base_price}
                        helperText={errors.base_price}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><AccountBalanceWallet fontSize="small" /></InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <TextField
                        label="Plan description"
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        value={formData.description}
                        onChange={(e) => handleFieldChange('description', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {featureType.erp && (
                <Paper elevation={0} variant="outlined" sx={{ ...sectionCardSx, p: { xs: 2, sm: 2.5 } }}>
                  <Typography variant="subtitle2" sx={{ color: '#0a0a0a', fontWeight: 700, mb: 0.5 }}>
                    ERP modules for this plan
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 2 }}>
                    Module checklist tied to the ERP side of agreements—not the same as other services below.
                  </Typography>

  {erpCategoryRows.map((row, rowIdx) => (
    <Grid container spacing={2} key={`erp-cat-row-${rowIdx}`} sx={{ mb: 2 }}>
      {row.map(([category, items]) => {
        const categoryIds = items.map((f) => Number(f.id));
        const selectedInCategory = categoryIds.filter((id) => selectedErpFeatures.includes(id)).length;
        const allSelected = categoryIds.length > 0 && selectedInCategory === categoryIds.length;
        const partiallySelected = selectedInCategory > 0 && !allSelected;
        return (
          <Grid item xs={12} md={4} key={category}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                height: '100%',
                minHeight: 220,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                  pb: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {category}
                </Typography>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={partiallySelected}
                      onChange={(e) => {
                        setSelectedErpFeatures((prev) => {
                          const prevSet = new Set(prev);
                          if (e.target.checked) {
                            categoryIds.forEach((id) => prevSet.add(id));
                          } else {
                            categoryIds.forEach((id) => prevSet.delete(id));
                          }
                          return Array.from(prevSet);
                        });
                      }}
                    />
                  }
                  label={<Typography variant="caption">All</Typography>}
                />
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 320, pr: 0.5 }}>
                {items.map((feature) => (
                  <FormControlLabel
                    key={feature.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      ml: 0,
                      mb: 0.5,
                    }}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedErpFeatures.includes(Number(feature.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedErpFeatures(prev => (prev.includes(Number(feature.id)) ? prev : [...prev, Number(feature.id)]));
                          } else {
                            setSelectedErpFeatures(prev => prev.filter(id => id !== Number(feature.id)));
                          }
                        }}
                      />
                    }
                    label={<Typography variant="body2">{feature.name}</Typography>}
                  />
                ))}
              </Box>
            </Paper>
          </Grid>
        );
      })}
      {Array.from({ length: Math.max(0, 3 - row.length) }).map((_, padIdx) => (
        <Grid item xs={12} md={4} key={`erp-pad-${rowIdx}-${padIdx}`} sx={{ display: { xs: 'none', md: 'block' } }} />
      ))}
    </Grid>
  ))}
                </Paper>
                )}

                {featureType.digital && (
                <Paper elevation={0} variant="outlined" sx={{ ...sectionCardSx, p: { xs: 2, sm: 2.5 } }}>
                  <Typography variant="subtitle2" sx={{ color: '#0a0a0a', fontWeight: 700, mb: 0.5 }}>
                    Other services for this plan (non-ERP)
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 2 }}>
                    Separate from ERP modules. These follow their own calculation types when used on agreements.
                  </Typography>

                  <Grid container spacing={2}>
                    {DigitalMarketingFeatureList.map((feature) => (
                      <Grid item xs={12} sm={6} md={4} key={feature.id}>
                        <Box
                          sx={{
                            transition: '0.2s',
                            '&:hover': { bgcolor: 'action.hover', borderRadius: 1 }
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedServices[feature.id] || false}
                                onChange={(e) => {
                                  setSelectedServices(prev => ({
                                    ...prev,
                                    [feature.id]: e.target.checked
                                  }));
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">{feature.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ₹{feature.price} | {{
                                    yearwise: 'Year Wise',
                                    monthwise: 'Month Wise',
                                    studentcount: 'Student Count',
                                  }[feature.calculation_type] || 'Month Wise'}
                                </Typography>
                              </Box>
                            }
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
                )}

                <Paper elevation={0} variant="outlined" sx={sectionCardSx}>
                  <Typography variant="subtitle2" sx={{ color: '#0a0a0a', fontWeight: 700 }}>
                    Tax settings
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
                    Default tax rate and GST applicability for this plan.
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small" variant="outlined" error={!!errors.default_tax} required>
                        <InputLabel>Default tax rate</InputLabel>
                        <Select
                          value={formData.default_tax}
                          onChange={(e) => handleFieldChange('default_tax', e.target.value)}
                          label="Default tax rate"
                        >
                          {taxList.map((tax) => (
                            <MenuItem key={tax.id} value={tax.id}>{tax.name} ({tax.percent}%)</MenuItem>
                          ))}
                        </Select>
                        {errors.default_tax && <FormHelperText>{errors.default_tax}</FormHelperText>}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.default_is_gst_applicable}
                            onChange={(e) => handleFieldChange('default_is_gst_applicable', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Is GST applicable"
                      />
                    </Grid>
                  </Grid>
                </Paper>

                <Divider sx={{ my: 2 }} />

                <Box display="flex" justifyContent="flex-end" gap={2} flexWrap="wrap">
                  <Button variant="outlined" onClick={resetForm} sx={{ textTransform: 'none', borderRadius: 2, px: 2 }}>
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={submitDisable}
                    onClick={submit}
                    sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
                  >
                    {submitDisable ? (isEditMode ? 'Updating...' : 'Submitting...') : (isEditMode ? 'Update plan' : 'Submit')}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}

export default ServicePlanAdd;
